import React, { useEffect, useState } from 'react';
import { IonCard, IonCardContent, IonCardHeader } from '@ionic/react';
import FilterForm, { FilterData } from '../../../FilterForm/FilterForm';
import Chart from '../../../Chart/Chart';
import { useReportData } from '../../../../api/hooks/useReportData';
import './ReportCard.css';
import Help from '../../../Help/Help';
// import arrowRightIcon from '../../icons/arrow-right.svg';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { useFilterValuesByReportId } from '../../../../hooks/useFilterValuesByReportId';
import { useGlobalFilterValues } from '../../../../hooks/useGlobalFilterValues';
import { useReportById } from '../../../../hooks/useReportById';
import { useUserState } from '../../../../hooks/context/useUserState';
import { ReportDataContentBody } from '../../../../api/types/generated/report_data_post.content';
import Table from '../../../Table/Table';
import ReportIcon from '../../../ReportIcon/ReportIcon';
import { SET_REPORT_FILTER_VALUES } from '../../../../constants';
import { getFiltersButtonText } from '../../../../utils/filters/getFiltersButtonText';
import { useTranslate } from '../../../../hooks/context/useTranslate';
import Button from '../../../Button/Button';
import Message from '../../../Message/Message';
import { getTranslatedErrorMessage } from '../../../../api/utils/getTranslatedErrorMessage';
import SeptimLoadingSpinner from '../../../Manager/SeptimLoadingSpinner/SeptimLoadingSpinner';

interface IGraphCardProps {
  id: string;
  windowWidth: number;
  reportsInRow: any[] | undefined;
  onReportDataFetched: (value: string) => void;
  onReportDataLoading: (value: string) => void;
  allReportsFetched: boolean;
}

const ReportCard = ({
  id,
  windowWidth,
  reportsInRow,
  onReportDataFetched,
  onReportDataLoading,
  allReportsFetched,
}: IGraphCardProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { dispatch } = useUserState();
  const globalFilterValues = useGlobalFilterValues();
  const filterValues = useFilterValuesByReportId(id);
  const report = useReportById(id);
  const { data, fetch, error, isLoading } = useReportData(id);
  const { t } = useTranslate();

  const fetchReportData = (report, filters) => {
    const body: ReportDataContentBody = {
      filters,
    };
    if (report.dataStyle && report.dataStyle[0]) {
      body.reportDataStyleId = report.dataStyle[0].id;
    }
    fetch(body);
  };

  useEffect(() => {
    if (report) {
      const values = [...globalFilterValues, ...(filterValues || [])];
      // Remove properties that I don't want to send to API, such as namedRange
      const valuesFiltered = values.map((filterData) => ({
        id: filterData.id,
        values: filterData.values,
      }));
      fetchReportData(report, valuesFiltered);
    }
  }, [filterValues, globalFilterValues]);

  useEffect(() => {
    if (data || error) {
      onReportDataFetched(id);
    }
  }, [data, error]);

  useEffect(() => {
    if (isLoading) {
      onReportDataLoading(id);
    }
  }, [isLoading]);

  const handleSubmit = (filterData: FilterData[]) => {
    dispatch({
      type: SET_REPORT_FILTER_VALUES,
      id,
      filterData,
    });
  };
  const filtersButtonText = getFiltersButtonText(
    report?.filters,
    filterValues || []
  );

  if (!allReportsFetched) {
    return <></>;
  }

  if (error) {
    const errorMessage = getTranslatedErrorMessage(error, t);
    return (
      <IonCard className={'error-card'}>
        <h2>
          <Message id="reportDataError" />
        </h2>
        <p>{errorMessage}</p>
        <div>
          <Button onClick={() => location.reload()}>
            <Message id="tryAgain" />
          </Button>
        </div>
      </IonCard>
    );
  }

  if (!report) {
    return (
      <IonCard className={'graph-card'}>
        <div className="report-data-loading">
          <SeptimLoadingSpinner />
        </div>
      </IonCard>
    );
  }

  const emptyData =
    data &&
    (!data.datasets[0].reportData || !data.datasets[0].reportData.length);
  const hideDescription =
    !report.description &&
    (emptyData ||
      (reportsInRow &&
        reportsInRow.every((rowReport) => !rowReport.description)));

  return (
    <IonCard className={'graph-card'}>
      <Tabs defaultIndex={0} onSelect={(index) => setSelectedTab(index)}>
        <TabList>
          {report.displayStyle &&
            report.displayStyle.map((style, index) => (
              <Tab key={style.id}>
                <ReportIcon type={style.type} active={index === selectedTab} />
              </Tab>
            ))}
        </TabList>
        {report.displayStyle &&
          report.displayStyle.map((style) => (
            <TabPanel key={style.id}>
              <IonCardHeader
                style={{
                  width: `calc(100% - ${report.displayStyle ? report.displayStyle.length * 37 + 15 : 30}px)`,
                  marginBottom: hideDescription && !emptyData ? '15px' : 0,
                }}
              >
                <Help
                  help={
                    report.annotation && !report.description
                      ? report.annotation
                      : undefined
                  }
                >
                  <h2>{report.label}</h2>
                </Help>
              </IonCardHeader>
              {!hideDescription && (
                <IonCardContent>
                  <Help
                    help={
                      report.description && report.annotation
                        ? report.annotation
                        : undefined
                    }
                  >
                    <p>{report.description}</p>
                  </Help>
                </IonCardContent>
              )}
              {!data ? (
                <div className="report-data-loading">
                  <h2>
                    <Message id="reportDataLoading" />
                  </h2>
                  <SeptimLoadingSpinner />
                </div>
              ) : emptyData ? (
                <div className="empty-report">
                  <ReportIcon type={style.type} active />
                  <h2>{t('noRecordsReport')}</h2>
                </div>
              ) : (
                <div className="report-container">
                  {style.type === 'table' ? (
                    <Table data={data} id={id} />
                  ) : (
                    <Chart
                      data={data}
                      displayStyle={style}
                      windowWidth={windowWidth}
                    />
                  )}
                </div>
              )}
              <div className="card-footer">
                {report.filters && report.filters.length !== 0 && (
                  <FilterForm
                    onSubmit={handleSubmit}
                    filters={report.filters.filter(
                      (filter) =>
                        !(
                          ['SELECT', 'MULTISELECT'].includes(filter.type) &&
                          // @ts-expect-error throws availableValues typescript error
                          !filter.availableValues?.length
                        )
                    )}
                    values={filterValues || []}
                    buttonText={filtersButtonText}
                  />
                )}
                {/* <IonButton fill="clear" className="detail-btn">
                  <Message id="detail" />
                  <img src={arrowRightIcon} alt="arrow right" />
                </IonButton> */}
              </div>
            </TabPanel>
          ))}
      </Tabs>
    </IonCard>
  );
};

export default ReportCard;
